import { useState, type ImgHTMLAttributes } from "react";

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  eager?: boolean;
  /** Optional WebP srcset string. When provided a <picture> wrapper is rendered. */
  webpSrcSet?: string;
};

/**
 * Performance-optimized image:
 * - Optional WebP <picture> source for modern browsers, JPG fallback for the rest
 * - Shimmer skeleton until loaded (no CLS — width/height required)
 * - Async decoding by default
 * - Lazy by default unless eager=true (above the fold)
 * - fetchpriority high for eager images
 */
export function SmartImage({
  eager = false,
  className = "",
  onLoad,
  webpSrcSet,
  sizes,
  srcSet,
  ...rest
}: Props) {
  const [loaded, setLoaded] = useState(false);

  const img = (
    <img
      {...rest}
      srcSet={srcSet}
      sizes={sizes}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={eager ? "high" : "auto"}
      onLoad={(e) => {
        setLoaded(true);
        onLoad?.(e);
      }}
      className={`${className} ${loaded ? "img-loaded opacity-100" : "opacity-0"} transition-opacity duration-500`}
    />
  );

  if (!webpSrcSet) return img;

  return (
    <picture>
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      {img}
    </picture>
  );
}
